import React from 'react';

import Typography from '@material-ui/core/Typography';
import { makeStyles, Theme } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Divider from '@material-ui/core/Divider';
import Button from '@material-ui/core/Button';
import ComputerIcon from '@material-ui/icons/Computer';
import SmartphoneIcon from '@material-ui/icons/Smartphone';
import InfoIcon from '@material-ui/icons/Info';
import ContactMailIcon from '@material-ui/icons/ContactMail';
import config from 'config';

const useStyles = makeStyles((theme: Theme) => {
  return {
    root: {
      margin: theme.spacing(4) + theme.spacing(0.5)
    },
    appName: {
      marginTop: theme.spacing(3.5),
      marginBottom: theme.spacing(1)
    },
    appType: {
      color: '#696969',
      marginBottom: theme.spacing(1)
    },
    appDescription: {
      marginBottom: theme.spacing(1.5)
    },
    header: {
      display: 'flex',
      paddingRight: theme.spacing(4),
      paddingBottom: theme.spacing(4) + theme.spacing(0.25)
    },
    button: {
      marginRight: theme.spacing(1.5)
    },
    subsectionContainer: {
      marginTop: theme.spacing(3),
      paddingBottom: theme.spacing(3),
      marginLeft: theme.spacing(7),
      [theme.breakpoints.down('md')]: {
        display: 'inline-block'
      }
    },
    link: {
      textDecoration: 'none'
    },
    subsectionButtonContainer: {
      display: 'inline-block',
      float: 'right',
      marginRight: theme.spacing(7)
    },
    subsection: {
      display: 'inline-block'
    },
    imageContainer: {
      position: 'relative',
      marginTop: theme.spacing(4) + theme.spacing(0.25),
      height: 'auto',
      width: 300,
      minWidth: '160px',
      '& img': {
        position: 'absolute',
        top: 0,
        bottom: 0,
        right: 0,
        left: 0,
        maxWidth: '100%',
        maxHeight: '100%',
        margin: 'auto'
      }
    }
  };
});

interface SubSectionProps
  extends React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement> {
  classes?: { [key: string]: string };
  header?: React.ReactNode;
  details?: React.ReactNode;
}

const Subsection: React.FC<SubSectionProps> = ({ classes = {}, header, details }) => {
  return (
    <div className={classes.subsection}>
      <Typography variant="subtitle1">{header}</Typography>
      <Typography variant="body1">{details}</Typography>
    </div>
  );
};

interface HeaderDetailsSectionProps
  extends React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement> {
  classes?: { [key: string]: string };
  appDescription: React.ReactNode;
  appName: React.ReactNode;
  appType: React.ReactNode;
  accessButtonOneLabel?: React.ReactNode;
  accessButtonTwoLabel?: React.ReactNode;
  accessButtonOneLink?: string | null | undefined;
  accessButtonTwoLink?: string | null | undefined;
}
const HeaderDetailsSection: React.FC<HeaderDetailsSectionProps> = ({
  className,
  appName,
  appType,
  appDescription,
  accessButtonOneLabel,
  accessButtonTwoLabel,
  accessButtonOneLink,
  accessButtonTwoLink,
  classes = {}
}) => {
  return (
    <div className={className}>
      <Typography className={classes.appName} variant="h3">
        {appName}
      </Typography>
      <Typography className={classes.appType} variant="subtitle2">
        {appType}
      </Typography>
      <Typography className={classes.appDescription}>{appDescription}</Typography>
      {accessButtonOneLink && (
        <a
          href={accessButtonOneLink}
          className={classes.link}
          target="_blank"
          rel="noopener noreferrer"
        >
          <Button
            className={classes.button}
            variant="contained"
            color="secondary"
            size="small"
            disableElevation
            startIcon={<ComputerIcon />}
          >
            {accessButtonOneLabel}
          </Button>
        </a>
      )}
      {accessButtonTwoLink && (
        <a
          href={accessButtonTwoLink}
          className={classes.link}
          target="_blank"
          rel="noopener noreferrer"
        >
          <Button
            className={classes.button}
            variant="contained"
            color="secondary"
            size="small"
            disableElevation
            startIcon={<SmartphoneIcon />}
          >
            {accessButtonTwoLabel}
          </Button>
        </a>
      )}
    </div>
  );
};

export interface ApplicationDetailsProps {
  appImage?: string;
  appName: React.ReactNode;
  appType: React.ReactNode;
  appDescription?: React.ReactNode;
  sectionOneHeader?: React.ReactNode;
  sectionOneDetails?: React.ReactNode;
  sectionTwoHeader?: React.ReactNode;
  sectionTwoDetails?: React.ReactNode;
  sectionOneButtonLabel?: React.ReactNode;
  sectionTwoButtonLabel?: React.ReactNode;
  sectionOneButtonLink?: string;
  sectionTwoButtonLink?: string;
  accessButtonOneLabel?: React.ReactNode;
  accessButtonTwoLabel?: React.ReactNode;
  accessButtonOneLink?: string | null | undefined;
  accessButtonTwoLink?: string | null | undefined;
}
const ApplicationDetails: React.FC<ApplicationDetailsProps> = ({
  appImage,
  appName,
  appType,
  appDescription,
  sectionOneHeader,
  sectionOneDetails,
  sectionOneButtonLabel = 'Learn More',
  sectionTwoHeader,
  sectionTwoDetails,
  sectionTwoButtonLabel = 'Contact Us',
  sectionOneButtonLink,
  sectionTwoButtonLink,
  accessButtonOneLabel,
  accessButtonTwoLabel,
  accessButtonOneLink,
  accessButtonTwoLink
}) => {
  const classes = useStyles();

  const handleContactEmail = (): Window | null => window.open(config.productSupportUrl, '_blank');

  return (
    <Paper className={classes.root}>
      <div className={classes.header}>
        {appImage && (
          <div className={classes.imageContainer}>
            <img src={appImage} alt={'app'} />
          </div>
        )}
        <HeaderDetailsSection
          classes={classes}
          appName={appName}
          appType={appType}
          appDescription={appDescription}
          accessButtonOneLabel={accessButtonOneLabel}
          accessButtonTwoLabel={accessButtonTwoLabel}
          accessButtonOneLink={accessButtonOneLink}
          accessButtonTwoLink={accessButtonTwoLink}
        />
      </div>
      <Divider />
      <div className={classes.subsectionContainer}>
        <Subsection classes={classes} header={sectionOneHeader} details={sectionOneDetails} />
        {sectionOneButtonLink && (
          <div className={classes.subsectionButtonContainer}>
            <a
              href={sectionOneButtonLink}
              className={classes.link}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button color="primary" variant="contained" disableElevation startIcon={<InfoIcon />}>
                {sectionOneButtonLabel}
              </Button>
            </a>
          </div>
        )}
      </div>
      <Divider />
      <div className={classes.subsectionContainer}>
        <Subsection classes={classes} header={sectionTwoHeader} details={sectionTwoDetails} />
        <div className={classes.subsectionButtonContainer}>
          <a
            href={sectionTwoButtonLink}
            className={classes.link}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Button
              variant="outlined"
              color="secondary"
              disableElevation
              onClick={handleContactEmail}
              startIcon={<ContactMailIcon />}
            >
              {sectionTwoButtonLabel}
            </Button>
          </a>
        </div>
      </div>
    </Paper>
  );
};

export default ApplicationDetails;
